import { HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, defer, finalize, map, shareReplay, switchMap, throwError, type Observable } from 'rxjs';

import { NotificationType } from '../enums/common-enum';
import { AppService } from '../services/app.service';
import { StorageKey, StorageService } from '../services/storage.service';
import { TokenRefreshService } from '../services/token-refresh.service';

let logout$: Observable<void> | null = null;

/**
 * Pre-login endpoints never need the access token. Sending a stale one left in
 * storage would only get the request rejected. Matched on the path suffix so
 * e.g. admin/account/register-by-admin still gets its token.
 */
const PUBLIC_AUTH_PATH_SUFFIXES = [
  '/account/login',
  '/account/verify-otp',
  '/account/register',
  '/account/verify-registration-otp',
  '/account/resend-otp',
  '/account/forgot-password',
  '/account/validate-forgot-password-link',
  '/account/reset-password',
];

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const storageService = inject(StorageService);
  const tokenRefreshService = inject(TokenRefreshService);
  const appService = inject(AppService);
  const isRefreshCall = req.url.includes('/refresh-token');
  const isLoginCall = req.url.includes('/account/login');
  const requestPath = req.url.split('?')[0].toLowerCase();
  const isPublicAuthCall = PUBLIC_AUTH_PATH_SUFFIXES.some((suffix) => requestPath.endsWith(suffix));

  // An expired access token is still a session: the refresh-token cookie can renew it.
  // Gating on isLoggedIn() here would skip the refresh exactly when it's needed.
  const hasStoredToken = (): boolean => {
    const loginData = storageService.getValue(
      StorageKey.loginData
    ) as { JWTToken?: string } | null;
    return Boolean(loginData?.JWTToken);
  };

  const withAuthHeaders = (request: HttpRequest<unknown>): HttpRequest<unknown> => {
    const headers: Record<string, string> = {};

    const clientOrigin =
      typeof window !== 'undefined' ? window.location.origin : '';

    if (clientOrigin) {
      headers['X-Client-Origin'] = clientOrigin;
    }

    const loginData = storageService.getValue(
      StorageKey.loginData
    ) as { JWTToken?: string } | null;

    if (loginData?.JWTToken && !isPublicAuthCall) {
      headers['Authorization'] = `Bearer ${loginData.JWTToken}`;
    }

    const selectedUserObj = storageService.getValue(
      StorageKey.selectedUserId
    ) as { value?: string } | null;

    if (selectedUserObj) {
      headers['SelectedUserId'] = selectedUserObj.value ?? '';
    }

    return Object.keys(headers).length > 0
      ? request.clone({ setHeaders: headers })
      : request;
  };

  const forceLogout = () => {
    if (!logout$) {
      logout$ = defer(() => {
        storageService.clearStorage();
        appService.showNotification(
          'Session expired',
          'Your session has expired. Please log in again.',
          NotificationType.WARNING
        );
        return router.navigate(['/login']);
      }).pipe(
        map(() => undefined),
        finalize(() => {
          logout$ = null;
        }),
        shareReplay(1)
      );
    }

    logout$.subscribe();
  };

  const authReq = withAuthHeaders(req);

  return next(authReq).pipe(
    // Legacy: older API builds answered an expiring token with 200 + { tokenExpired }. Current builds send 401,
    // handled in catchError below. Kept so a frontend deployed ahead of the API still works.
    switchMap((event) => {
      const tokenExpired =
        !isRefreshCall &&
        !isLoginCall &&
        hasStoredToken() &&
        event instanceof HttpResponse &&
        Boolean((event.body as { tokenExpired?: boolean } | null)?.tokenExpired);

      if (!tokenExpired) {
        return [event];
      }

      return tokenRefreshService.refreshAccessToken().pipe(
        switchMap((newAccessToken) => {
          if (!newAccessToken) {
            forceLogout();
            return throwError(() => new Error('Session expired'));
          }

          return next(withAuthHeaders(req));
        })
      );
    }),
    catchError((error) => {
      if (error.status === 401 && !isRefreshCall && !isPublicAuthCall && hasStoredToken()) {
        return tokenRefreshService.refreshAccessToken().pipe(
          // The refresh call itself failed (offline, 5xx, 429): the session may be fine, so surface the
          // original error instead of logging the user out.
          catchError(() => throwError(() => error)),
          switchMap((newAccessToken) => {
            if (!newAccessToken) {
              forceLogout();
              return throwError(() => error);
            }

            return next(withAuthHeaders(req)).pipe(
              catchError((retryError) => {
                // Only a second 401 with a fresh token means the session is gone; a 400/500 on the retried
                // request is an ordinary failure for the caller to handle.
                if (retryError?.status === 401) {
                  forceLogout();
                }
                return throwError(() => retryError);
              })
            );
          })
        );
      }

      return throwError(() => error);
    })
  );
};